import type { ToggleLikePayload } from '@/api/user/types/toggle-like-payload';
import { httpService } from '../common/http.service';
import type { ApiResponse } from '../common/types/api-response';
import type { MiniUser, User } from './types/user';
import type { UserProfile } from './types/user-profile';
import type { ToggleHidePayload } from '@/api/user/types/toggle-hide-payload';
import logger, { setLoggerUser } from '@/services/logger';
import { setMetaAccount } from '@/services/meta-app-events';
import { setSessionReplayAccount } from '@/services/session-replay';
import {
  applyServerPrivacyConsents,
  loadPendingRevocations,
  retryPendingRevocations,
} from '@/services/privacy-consents';
import type { OtherUser } from '@/api/user/types/other-user';
import type { FollowResponsePayload } from '@/api/user/types/follow-response-payload';
import type { LikePlanPayload } from '@/api/user/types/like-plan-payload';

const ENDPOINT = 'user';

export async function getUser() {
  try {
    const res = await httpService.get<ApiResponse<User>>(ENDPOINT);
    const user = res.data.data;
    setLoggerUser(user);
    // A revocation the backend has not stored yet stays in force: read it before Meta looks.
    await loadPendingRevocations(user.id);
    // The account's consents come only from here and the consent endpoint; Meta reads them.
    applyServerPrivacyConsents(user.id, user.privacyConsents);
    // Meta consent is per account and age-gated (birth date is read locally, never sent).
    void setMetaAccount(user);
    // Session Replay records only with this account's stored consent (and stops without it).
    setSessionReplayAccount(user);
    void retryPendingRevocations(user.id);
    return user;
  } catch (error) {
    logger.error({
      message: '[UserAPI] getUser error',
      data: {},
      error: error as Error,
    });
    throw error;
  }
}

export async function getUsers(txt: string, signal?: AbortSignal) {
  try {
    const res = await httpService.get<ApiResponse<MiniUser[]>>(`${ENDPOINT}/search`, { q: txt }, { signal });
    return res.data.data;
  } catch (error) {
    logger.error({
      message: '[UserAPI] getUsers error',
      data: {},
      error: error as Error,
    });
    throw error;
  }
}

export async function getPeopleYouMayKnow() {
  try {
    const res = await httpService.get<ApiResponse<MiniUser[]>>(`${ENDPOINT}/recommendations`);
    return res.data.data;
  } catch (error) {
    logger.error({
      message: '[UserAPI] getPeopleYouMayKnow error',
      data: {},
      error: error as Error,
    });
    throw error;
  }
}

export async function getUserById(id: string) {
  try {
    const res = await httpService.get<ApiResponse<OtherUser>>(`${ENDPOINT}/${id}`);
    return res.data.data;
  } catch (error) {
    logger.error({
      message: `[UserAPI] getUserById error ${id}`,
      data: { id },
      error: error as Error,
    });
    throw error;
  }
}

export async function editUserProfile(profile: UserProfile) {
  try {
    const res = await httpService.post<ApiResponse<User>>(`${ENDPOINT}/profile`, {
      profile: profile,
    });
    return res.data.data;
  } catch (error) {
    logger.error({
      message: '[UserAPI] editUserProfile error',
      // The profile holds health data (date of birth, weight, injuries): never logged.
      data: {},
      error: error as Error,
    });
    throw error;
  }
}

export async function sendFollowRequest(id: string) {
  try {
    const res = await httpService.post<ApiResponse<undefined>>(`${ENDPOINT}/follow`, { userId: id });
    return res.data.success;
  } catch (error) {
    logger.error({
      message: `[UserAPI] sendFollowRequest error ${id}`,
      data: { id },
      error: error as Error,
    });
    throw error;
  }
}

export async function unfollowUser(id: string) {
  try {
    const res = await httpService.post<ApiResponse<undefined>>(`${ENDPOINT}/unfollow`, { userId: id });
    return res.data.success;
  } catch (error) {
    logger.error({
      message: `[UserAPI] sendFollowRequest error ${id}`,
      data: { id },
      error: error as Error,
    });
    throw error;
  }
}

export async function toggleLikePlan(payload: LikePlanPayload) {
  try {
    const res = await httpService.post<ApiResponse<{ likePlans: string[] }>>(`plans/${payload.planId}/like`, {
      liked: payload.liked,
    });
    return res.data.data;
  } catch (error) {
    logger.error({
      message: `[UserAPI] toggleLikeWorkout error ${payload.planId}`,
      data: { payload },
      error: error as Error,
    });
    throw error;
  }
}

export async function toggleLikeExercise(payload: ToggleLikePayload) {
  try {
    const res = await httpService.post<ApiResponse<{ likedExercises: string[] }>>(
      `exercises/${payload.exerciseId}/like`,
      {
        liked: payload.liked,
      }
    );
    return res.data.data;
  } catch (error) {
    logger.error({
      message: `[UserAPI] toggleLikeExercise error ${payload.exerciseId}`,
      data: { payload },
      error: error as Error,
    });
    throw error;
  }
}

export async function toggleHideExercise(payload: ToggleHidePayload) {
  try {
    const res = await httpService.post<ApiResponse<{ hiddenExercises: string[] }>>(
      `exercises/${payload.exerciseId}/hide`,
      {
        hidden: payload.hidden,
      }
    );
    return res.data.data;
  } catch (error) {
    logger.error({
      message: `[UserAPI] toggleHideExercise error ${payload.exerciseId}`,
      data: { payload },
      error: error as Error,
    });
    throw error;
  }
}

export const followResponse = async (payload: FollowResponsePayload) => {
  try {
    const res = await httpService.post<ApiResponse<undefined>>(`${ENDPOINT}/follow-response`, payload);
    return res.data;
  } catch (err) {
    logger.error({
      message: '[UserAPI] followResponse error',
      data: { payload },
      error: err as Error,
    });
    throw err;
  }
};

export const uploadUserAvatar = async (file: File | null) => {
  try {
    const formData = new FormData();
    formData.append('file', file || '');
    const res = await httpService.postFormData<ApiResponse<User>>(`${ENDPOINT}/avatar`, formData);
    return res.data.data;
  } catch (error) {
    logger.error({
      message: '[UserAPI] uploadUserAvatar error',
      data: {},
      error: error as Error,
    });
    throw error;
  }
};

export const removeUser = async () => {
  try {
    await httpService.delete(ENDPOINT);
  } catch (error) {
    logger.error({
      message: '[UserAPI] removeUser error',
      data: {},
      error: error as Error,
    });
    throw error;
  }
};

export const updateUserFcmToken = async (fcmToken: string) => {
  try {
    await httpService.post(`${ENDPOINT}/fcm-token`, { fcmToken });
  } catch (error) {
    logger.error({
      message: '[UserAPI] updateUserFcmToken error',
      data: {},
      error: error as Error,
    });
    throw error;
  }
};

export const enablePushNotification = async (payload: { enabled: boolean }) => {
  try {
    await httpService.put(`${ENDPOINT}/settings`, { isPushNotificationEnabled: payload.enabled });
  } catch (error) {
    logger.error({
      message: '[UserAPI] enablePushNotification error',
      data: { enable: payload.enabled },
      error: error as Error,
    });
    throw error;
  }
};

export const enableSystemSound = async (payload: { enabled: boolean }) => {
  try {
    await httpService.put(`${ENDPOINT}/settings`, { isSystemSoundEnabled: payload.enabled });
  } catch (error) {
    logger.error({
      message: '[UserAPI] enableSystemSound error',
      data: { enable: payload.enabled },
      error: error as Error,
    });
    throw error;
  }
};

export const setFeedPostVisibility = async (payload: { feedPostVisibility: 'everyone' | 'followers_only' }) => {
  try {
    await httpService.put(`${ENDPOINT}/settings`, {
      feedPostVisibility: payload.feedPostVisibility,
    });
  } catch (error) {
    logger.error({
      message: '[UserAPI] setFeedPostVisibility error',
      data: { feedPostVisibility: payload.feedPostVisibility },
      error: error as Error,
    });
    throw error;
  }
};
